import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { mapProductsToItemData } from '@/shared/api/mappers/product';
import type { ProductStatus } from '@/shared/api/types/generated';
import { useGetProductsByStatusQuery } from '@/shared/hooks/queries/generated';
import type { ProductItemData } from '@/shared/types/product';

interface UseProductsByStatusParams {
  analysisId: string;
  status: ProductStatus;
  enabled?: boolean;
}

interface UseProductsByStatusReturn {
  allProducts: ProductItemData[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useProductsByStatus = ({
  analysisId,
  status,
  enabled = true,
}: UseProductsByStatusParams): UseProductsByStatusReturn => {
  // Récupérer tous les produits du backend
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['productsByStatus', analysisId, status],
    queryFn: () =>
      useGetProductsByStatusQuery.fetcher({
        analysisId,
        status,
      })(),
    enabled: enabled && !!analysisId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Mapper tous les produits
  const allProducts = useMemo(() => {
    return mapProductsToItemData(data?.getProductsByStatus ?? []);
  }, [data]);

  return {
    allProducts,
    isLoading,
    error: error ? 'Erreur lors de la récupération des produits' : null,
    refetch,
  };
};
